import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Play, Check, X, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { api } from '@/lib/api';
import { apiErrorMessage } from '@/lib/clients';
import { useHasPermission } from '@/lib/permissions';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

import type { WorkOrder } from '@logistech/ui-shared';

// Réponse de GET /work-orders/:id (maintenance-service, getWorkOrder)
interface WorkOrderDetail extends WorkOrder {
  notes: string | null;
  lines: { id: string; description: string; qty: number; unitPrice: number; total: number; currency: string }[];
}

export const WO_STATUS_LABELS: Record<WorkOrder['status'], string> = {
  DRAFT: 'Brouillon',
  SCHEDULED: 'Planifié',
  IN_PROGRESS: 'En cours',
  COMPLETED: 'Terminé',
  CANCELLED: 'Annulé',
};

export const WO_STATUS_VARIANT: Record<WorkOrder['status'], 'default' | 'secondary' | 'success' | 'warning' | 'destructive'> = {
  DRAFT: 'secondary',
  SCHEDULED: 'warning',
  IN_PROGRESS: 'default',
  COMPLETED: 'success',
  CANCELLED: 'destructive',
};

const money = (n: number, cur = 'GNF') => `${n.toLocaleString('fr-FR')} ${cur}`;

interface Props { workOrderId: string | null; onOpenChange: (open: boolean) => void }

/**
 * Suivi d'un ordre de travail : Planifié → En cours (coûts saisis ligne à ligne) → Terminé.
 * Le coût total est calculé par le service à la clôture, à partir des lignes.
 */
export function WorkOrderDetailDialog({ workOrderId, onOpenChange }: Props) {
  const qc = useQueryClient();
  const canWrite = useHasPermission('maintenance.write');
  const [line, setLine] = useState({ description: '', qty: '1', unitPrice: '' });

  const { data: wo } = useQuery({
    queryKey: ['work-order', workOrderId],
    queryFn: async () => (await api.get<WorkOrderDetail>(`/work-orders/${workOrderId}`)).data,
    enabled: !!workOrderId,
  });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['work-order', workOrderId] });
    void qc.invalidateQueries({ queryKey: ['work-orders'] });
    void qc.invalidateQueries({ queryKey: ['maintenance-plans'] });
  };

  const action = useMutation({
    mutationFn: (kind: 'start' | 'complete' | 'cancel') =>
      kind === 'complete'
        ? api.post(`/work-orders/${workOrderId}/complete`)
        : api.patch(`/work-orders/${workOrderId}`, { status: kind === 'start' ? 'IN_PROGRESS' : 'CANCELLED' }),
    onSuccess: (_r, kind) => {
      refresh();
      toast.success(kind === 'start' ? 'Intervention démarrée' : kind === 'complete' ? 'Intervention terminée' : 'Intervention annulée');
    },
    onError: (err: unknown) => toast.error(apiErrorMessage(err, 'Action impossible')),
  });

  const addLine = useMutation({
    mutationFn: () =>
      api.post(`/work-orders/${workOrderId}/lines`, {
        description: line.description.trim(),
        qty: Number(line.qty),
        unitPrice: Number(line.unitPrice),
      }),
    onSuccess: () => { refresh(); setLine({ description: '', qty: '1', unitPrice: '' }); },
    onError: (err: unknown) => toast.error(apiErrorMessage(err, 'Ligne non ajoutée')),
  });

  const removeLine = useMutation({
    mutationFn: (lineId: string) => api.delete(`/work-orders/${workOrderId}/lines/${lineId}`),
    onSuccess: refresh,
    onError: (err: unknown) => toast.error(apiErrorMessage(err, 'Suppression impossible')),
  });

  // Sans maintenance.write : consultation seule (coûts, statut), aucune action.
  const editable = canWrite && wo && (wo.status === 'DRAFT' || wo.status === 'SCHEDULED' || wo.status === 'IN_PROGRESS');
  const linesTotal = wo?.lines.reduce((s, l) => s + Number(l.total), 0) ?? 0;
  const lineValid = line.description.trim().length >= 2 && Number(line.qty) > 0 && line.unitPrice !== '' && Number(line.unitPrice) >= 0;

  return (
    <Dialog open={!!workOrderId} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <span className="font-mono">{wo?.reference}</span>
            {wo && <Badge variant={WO_STATUS_VARIANT[wo.status]}>{WO_STATUS_LABELS[wo.status]}</Badge>}
          </DialogTitle>
        </DialogHeader>

        {wo && (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-2">
              <div><span className="text-muted-foreground">Véhicule : </span>{wo.vehicle?.plate}</div>
              <div><span className="text-muted-foreground">Plan : </span>{wo.plan?.name ?? 'Ponctuel'}</div>
              <div className="col-span-2"><span className="text-muted-foreground">Intervention : </span>{wo.description ?? '—'}</div>
              <div><span className="text-muted-foreground">Prévue le : </span>{wo.scheduledAt ? new Date(wo.scheduledAt).toLocaleDateString('fr-FR') : '—'}</div>
              {wo.completedAt && (
                <div><span className="text-muted-foreground">Terminée le : </span>{new Date(wo.completedAt).toLocaleDateString('fr-FR')}</div>
              )}
            </div>

            <div>
              <p className="font-medium mb-1">Coûts</p>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Qté</TableHead>
                    <TableHead className="text-right">Prix unitaire</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    {editable && <TableHead />}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {wo.lines.map((l) => (
                    <TableRow key={l.id}>
                      <TableCell>{l.description}</TableCell>
                      <TableCell className="text-right">{Number(l.qty)}</TableCell>
                      <TableCell className="text-right">{money(Number(l.unitPrice), l.currency)}</TableCell>
                      <TableCell className="text-right">{money(Number(l.total), l.currency)}</TableCell>
                      {editable && (
                        <TableCell>
                          <Button size="sm" variant="ghost" onClick={() => { if (window.confirm('Supprimer cette ligne ?')) removeLine.mutate(l.id); }} aria-label="Supprimer la ligne">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  {wo.lines.length === 0 && (
                    <TableRow><TableCell colSpan={5} className="text-muted-foreground">Aucun coût saisi</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
              <p className="text-right font-semibold mt-1">Total : {money(wo.totalCost ?? linesTotal, wo.currency)}</p>
            </div>

            {editable && (
              <div className="flex gap-2 items-end">
                <Input
                  placeholder="Pièce ou main d'œuvre" value={line.description} className="flex-1"
                  onChange={(e) => setLine({ ...line, description: e.target.value })}
                />
                <Input
                  placeholder="Qté" inputMode="decimal" value={line.qty} className="w-20"
                  onChange={(e) => setLine({ ...line, qty: e.target.value })}
                />
                <Input
                  placeholder="Prix unit. (GNF)" inputMode="numeric" value={line.unitPrice} className="w-36"
                  onChange={(e) => setLine({ ...line, unitPrice: e.target.value })}
                />
                <Button size="sm" onClick={() => addLine.mutate()} disabled={!lineValid || addLine.isPending}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            )}

            {canWrite && (
              <div className="flex justify-end gap-2 pt-2 border-t">
                {editable && (
                  <Button variant="outline" onClick={() => action.mutate('cancel')} disabled={action.isPending}>
                    <X className="h-4 w-4 mr-1" /> Annuler l&apos;intervention
                  </Button>
                )}
                {(wo.status === 'DRAFT' || wo.status === 'SCHEDULED') && (
                  <Button onClick={() => action.mutate('start')} disabled={action.isPending}>
                    <Play className="h-4 w-4 mr-1" /> Démarrer
                  </Button>
                )}
                {wo.status === 'IN_PROGRESS' && (
                  <Button onClick={() => action.mutate('complete')} disabled={action.isPending}>
                    <Check className="h-4 w-4 mr-1" /> Terminer
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
